import { CardTable, Link } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CurrencyValue,
  WalletAddress,
  RelativeDateTime,
  TableSkeleton
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';

type LiquidationBuyersData = {
  address: string;
  market_address: string;
  underlying_address: string;
  underlying_symbol: string;
  market_symbol: string;
  wallet_address: string;
  base_amount: string | null;
  collateral_amount: string | null;
  order_index: string;
  datetime: string;
};

interface CollateralBuyersResponse extends PaginatedResponse<
  LiquidationBuyersData[]
> {
  market_address: string;
  market_symbol: string;
}

export default function CollateralBuyersTable({
  marketAddress,
  network
}: {
  marketAddress: string;
  network: string;
}) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-datetime'
  });

  const [data, error] = useApi<CollateralBuyersResponse>({
    url: `/markets/${marketAddress}/liquidations/collateral-buyers/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} />;
  }
  const columns: TableColumn<LiquidationBuyersData>[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`./${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Collateral Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_amount}
          options={{
            currency: {
              name: row.underlying_symbol,
              address: row.underlying_address,
              network: network
            }
          }}
        />
      ),
      sortingField: 'collateral_amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Base Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.base_amount}
          options={{
            currency: {
              name: data.extra?.market_symbol,
              address: data.extra?.market_address,
              network: network
            }
          }}
        />
      ),
      sortingField: 'base_amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <RelativeDateTime
          value={row.datetime}
          formatDateTimeOptions={{ toRelativeUnit: 'days' }}
        />
      ),
      sortingField: 'datetime',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <CardTable<LiquidationBuyersData>
      title="Collateral Buyers"
      rows={data['results']}
      columns={columns}
      getKey={(row) => row.order_index}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
